import { DataGridColumn } from 'components/Sov/components/SovEditGrid/components/DataGrid';
import * as Yup from 'yup';

import { numberMask, yearMask, zipCodeMask } from 'helpers/masks';
import { LocationOptionsData } from 'types/types.type';
import moment from 'moment';
import { STATE_LIST, YES_NO_OPTIONS } from '../../../../constants';
import { PropertyColumn } from '../../../../types/submission.type';
import { suppressUpDownEnterForDropDowns } from '../../../../helpers/sov';
import { ValueGetterParams } from 'ag-grid-enterprise';

const checkSomeValue = (v1?: number, v2?: number, v3?: number, v4?: number, v5?: number, v6?: number) => {
    return (
        (v1 ? parseLocaleNumber(v1) : 0) +
            (v2 ? parseLocaleNumber(v2) : 0) +
            (v3 ? parseLocaleNumber(v3) : 0) +
            (v4 ? parseLocaleNumber(v4) : 0) +
            (v5 ? parseLocaleNumber(v5) : 0) +
            (v6 ? parseLocaleNumber(v6) : 0) >
        0
    );
};

const columnDetails = (locationOptions: LocationOptionsData): Record<string, DataGridColumn> => ({
    REMOVE_ICON: {
        columnName: '',
        key: 'markedAsDeleted',
        inputType: 'icon',
        required: false,
        columnDef: {
            width: 50,
            headerName: '',
            field: 'markedAsDeleted',
            pinned: 'left',
            lockPinned: true,
        },
    },
    LOCATION_NAME: {
        columnName: 'Location Name',
        key: 'locationName',
        required: true,
        validation: Yup.string().required('Required').nullable(false),
        columnDef: {
            headerName: 'Location Name',
            field: 'locationName',
            width: 400,
            pinned: 'left',
            lockPinned: true,
        },
    },
    LOCATION_ADDRESS: {
        columnName: 'Location Address',
        key: 'address1',
        required: true,
        validation: Yup.string().required('Required').nullable(false),
        columnDef: {
            headerName: 'Location Address',
            field: 'address1',
            width: 400,
        },
    },
    LOCATION_ADDRESS_2: {
        columnName: 'Location Address 2',
        key: 'address2',
        required: false,
        columnDef: {
            headerName: 'Location Address 2',
            field: 'address2',
            width: 175,
        },
    },
    CITY: {
        columnName: 'City',
        key: 'city',
        required: true,
        validation: Yup.string().required('Required').nullable(false),
        columnDef: {
            headerName: 'City',
            field: 'city',
            width: 150,
        },
    },
    STATE: {
        columnName: 'State',
        key: 'state',
        required: true,
        inputType: 'drop-down',
        optionsList: STATE_LIST,
        validation: Yup.string().required('Required').oneOf(STATE_LIST),
        columnDef: {
            headerName: 'State',
            field: 'state',
            width: 130,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    ZIP: {
        columnName: 'Zip',
        key: 'zip',
        required: true,
        mask: zipCodeMask,
        validation: Yup.number().typeError('Please enter a number').required('Required').min(5).nullable(false),
        columnDef: {
            headerName: 'Zip',
            field: 'zip',
            width: 115,
        },
    },
    COUNTY: {
        columnName: 'County',
        key: 'county',
        required: true,
        validation: Yup.string().required('Required').nullable(false),
        columnDef: {
            headerName: 'County',
            field: 'county',
            width: 150,
        },
    },
    CLASS: {
        columnName: 'Class',
        key: 'locationClassType',
        width: 250,
        required: true,
        inputType: 'drop-down',
        options: locationOptions.LOCATION_CLASSES,
        validation: Yup.string()
            .required('Required')
            .oneOf(Object.keys(locationOptions.LOCATION_CLASSES), 'Must be one of the drop down values'),
        columnDef: {
            headerName: 'Class',
            field: 'locationClassType',
            width: 250,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    CONSTRUCTION_TYPE: {
        columnName: 'Construction Type',
        key: 'constructionType',
        width: 250,
        required: true,
        inputType: 'drop-down',
        options: locationOptions.LOCATION_CONSTRUCTION_TYPES,
        validation: Yup.string()
            .required('Required')
            .oneOf(Object.keys(locationOptions.LOCATION_CONSTRUCTION_TYPES), 'Must be one of the drop down values'),
        columnDef: {
            headerName: 'Construction Type',
            field: 'constructionType',
            width: 250,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    WIRING_TYPE: {
        columnName: 'Wiring Type',
        key: 'wiringType',
        width: 250,
        required: true,
        inputType: 'drop-down',
        options: locationOptions.LOCATION_WIRING_TYPES,
        validation: Yup.string()
            .required('Required')
            .oneOf(Object.keys(locationOptions.LOCATION_WIRING_TYPES), 'Must be one of the drop down values'),
        columnDef: {
            headerName: 'Wiring Type',
            field: 'wiringType',
            width: 250,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    YEAR_OF_CONSTRUCTION: {
        columnName: 'Year of Construction',
        key: 'yearOfConstruction',
        required: true,
        mask: yearMask,
        validation: Yup.number()
            .required('Required')
            .min(1900, 'Enter a year after 1900')
            .max(moment().add(2, 'months').year(), 'Enter a year before this year')
            .nullable(false),
        columnDef: {
            headerName: 'Year of Construction',
            field: 'yearOfConstruction',
            width: 140,
        },
    },
    OCC_PERCENT: {
        columnName: 'Occ %',
        key: 'occupancyPercentage',
        required: true,
        inputType: 'percentage',
        mask: numberMask,
        validation: Yup.number()
            .typeError('Please enter a number')
            .required('Required')
            .min(0)
            .max(100)
            .nullable(false),
        columnDef: {
            headerName: 'Occ %',
            field: 'occupancyPercentage',
            width: 100,
        },
    },
    NUMBER_OF_UNITS: {
        columnName: '# Units',
        key: 'numberOfUnits',
        required: true,
        mask: numberMask,
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .required('Required')
            .min(0)
            .nullable(false),
        columnDef: {
            headerName: '# Units',
            field: 'numberOfUnits',
            width: 110,
        },
    },
    NUMBER_OF_POOLS: {
        columnName: '# Pools',
        key: 'numberOfPools',
        width: 75,
        required: true,
        validation: Yup.number().typeError('Please enter a number').required('Required').min(0).nullable(false),
        columnDef: {
            headerName: '# Pools',
            field: 'numberOfPools',
            width: 110,
        },
    },
    NUMBER_OF_BUILDINGS: {
        columnName: '# Bldings',
        key: 'numberOfBuildings',
        required: true,
        mask: numberMask,
        validation: Yup.number().typeError('Please enter a number').required('Required').min(1).nullable(false),
        columnDef: {
            headerName: '# Bldings',
            field: 'numberOfBuildings',
            width: 120,
        },
    },
    NUMBER_OF_STORIES: {
        columnName: '# Stories',
        key: 'numberOfStories',
        required: true,
        mask: numberMask,
        validation: Yup.number().typeError('Please enter a number').required('Required').min(1).nullable(false),
        columnDef: {
            headerName: '# Stories',
            field: 'numberOfStories',
            width: 120,
        },
    },
    LAST_ROOF_REPLACEMENT_YEAR: {
        columnName: 'Yr of Last Full Roof Replacement',
        key: 'lastYearOfRoofReplacement',
        width: 140,
        required: true,
        mask: yearMask,
        validation: Yup.number()
            .transform((_, n) => (n ? Number(n) : null))
            .min(1900, 'Enter a year after 1900')
            .max(moment().add(2, 'months').year(), 'Enter a year before this year')
            .nullable(),
        columnDef: {
            headerName: 'Yr of Last Full Roof Replacement',
            field: 'lastYearOfRoofReplacement',
            width: 140,
        },
    },
    ROOF_TYPE: {
        columnName: 'Roof Type',
        key: 'roofType',
        required: true,
        inputType: 'drop-down',
        options: locationOptions.LOCATION_ROOF_TYPES,
        validation: Yup.string()
            .transform((_, n) => (n ? n.toString() : null))
            .oneOf([...Object.keys(locationOptions.LOCATION_ROOF_TYPES), null], 'Must be one of the drop down values')
            .nullable(),
        columnDef: {
            headerName: 'Roof Type',
            field: 'roofType',
            width: 250,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    ROOF_COVERING: {
        columnName: 'Roof Covering',
        key: 'roofCoveringType',
        required: true,
        inputType: 'drop-down',
        options: locationOptions.LOCATION_ROOF_COVERING_TYPES,
        validation: Yup.string()
            .transform((_, n) => (n ? n.toString() : null))
            .oneOf([...Object.keys(locationOptions.LOCATION_ROOF_COVERING_TYPES), null])
            .nullable(),
        columnDef: {
            headerName: 'Roof Covering',
            field: 'roofCoveringType',
            width: 270,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    SECTION_EIGHT_PERCENT: {
        columnName: '% Section 8',
        key: 'percentageSection8',
        required: true,
        inputType: 'percentage',
        mask: numberMask,
        validation: Yup.number()
            .typeError('Please enter a number')
            .required('Required')
            .min(0)
            .max(100)
            .nullable(false),
        columnDef: {
            headerName: '% Section 8',
            field: 'percentageSection8',
            width: 130,
        },
    },
    OWNER_OCCUPIED_PERCENT: {
        columnName: '% Owner Occupied',
        key: 'percentageOwnerOccupied',
        required: true,
        inputType: 'percentage',
        mask: numberMask,
        validation: Yup.number()
            .transform((_, n) => (n ? Number(n) : null))
            .typeError('Please enter a number')
            .min(0)
            .max(100)
            .nullable(),
        columnDef: {
            headerName: '% Owner Occupied',
            field: 'percentageOwnerOccupied',
            width: 120,
        },
    },
    OWNED_OR_MANAGED: {
        columnName: 'Owned or Managed',
        key: 'ownershipType',
        required: true,
        inputType: 'drop-down',
        options: locationOptions.LOCATION_OWNERSHIP_TYPES,
        validation: Yup.string()
            .transform((_, n) => (n ? n.toString() : null))
            .oneOf(
                [...Object.keys(locationOptions.LOCATION_OWNERSHIP_TYPES), null],
                'Must be one of the drop down values'
            )
            .nullable(),
        columnDef: {
            headerName: 'Owned or Managed',
            field: 'ownershipType',
            width: 170,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    NUMBER_OF_YEARS_OWNED_OR_MANAGED: {
        columnName: '# Years Owned or Managed',
        key: 'numberOfYearsOwnedManaged',
        required: true,
        mask: numberMask,
        validation: Yup.number()
            .transform((_, val) => (val ? Number(val) : null))
            .typeError('Please enter a number')
            .min(0)
            .nullable(),
        columnDef: {
            headerName: '# Years Owned or Managed',
            field: 'numberOfYearsOwnedManaged',
            width: 130,
        },
    },
    FLOOD_ZONE: {
        columnName: 'Flood Zone',
        key: 'floodZoneType',
        required: true,
        inputType: 'drop-down',
        options: locationOptions.LOCATION_FLOOD_ZONE_TYPES,
        validation: Yup.string()
            .transform((_, n) => (n ? n.toString() : null))
            .oneOf(
                [...Object.keys(locationOptions.LOCATION_FLOOD_ZONE_TYPES), null],
                'Must be one of the drop down values'
            )
            .nullable(),
        columnDef: {
            headerName: 'Flood Zone',
            field: 'floodZoneType',
            width: 150,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    HW_SMOKE_DET_ALL_UNITS: {
        columnName: 'HW Smoke Det All Units',
        key: 'hwSmokeDetectorAllUnits',
        required: true,
        inputType: 'drop-down',
        options: YES_NO_OPTIONS,
        validation: Yup.string().required('Required').oneOf(Object.keys(YES_NO_OPTIONS)),
        columnDef: {
            headerName: 'HW Smoke Det All Units',
            field: 'hwSmokeDetectorAllUnits',
            width: 130,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    A_S: {
        columnName: 'A/S',
        key: 'automaticSprinklers',
        required: true,
        inputType: 'drop-down',
        options: YES_NO_OPTIONS,
        validation: Yup.string().required('Required').oneOf(Object.keys(YES_NO_OPTIONS)),
        columnDef: {
            headerName: 'A/S',
            field: 'automaticSprinklers',
            width: 130,
            suppressKeyboardEvent: suppressUpDownEnterForDropDowns,
        },
    },
    A_S_PERCENT: {
        columnName: '% A/S',
        key: 'automaticSprinklersPercentage',
        required: true,
        inputType: 'percentage',
        mask: numberMask,
        validation: Yup.number()
            .transform((_, n) => (n ? Number(n) : null))
            .min(0)
            .max(100)
            .nullable(),
        columnDef: {
            headerName: '% A/S',
            field: 'automaticSprinklersPercentage',
            width: 110,
        },
    },
    TOTAL_SQUARE_FOOTAGE: {
        columnName: 'Total Square Footage',
        key: 'totalSquareFootage',
        required: true,
        mask: numberMask,
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .required('Required')
            .min(0)
            .nullable(false),
        columnDef: {
            headerName: 'Total Square Footage',
            field: 'totalSquareFootage',
            width: 125,
        },
    },
    BUILDING_VALUE: {
        columnName: 'Building Value',
        key: 'buildingValue',
        required: true,
        inputType: 'currency',
        mask: numberMask,
        validationSort: [
            ['buildingValue', 'biRentalIncomeValue'],
            ['buildingValue', 'personalPropertyValue'],
            ['buildingValue', 'otherValue'],
            ['buildingValue', 'grossSales'],
            ['buildingValue', 'plotSizeAcres'],
        ],
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : 0))
            .min(0)
            .when(
                ['biRentalIncomeValue', 'personalPropertyValue', 'otherValue', 'grossSales', 'plotSizeAcres'],
                (v1: number, v2: number, v3: number, v4: number, v5: number, schema: Yup.NumberSchema) => {
                    return schema.test({
                        test: (bv?: number) => checkSomeValue(v1, v2, v3, v4, v5, bv),
                        message: 'at least one value or sales or acres must have data',
                    });
                }
            ),
        columnDef: {
            headerName: 'Building Value',
            field: 'buildingValue',
            width: 160,
        },
    },
    BI_RENTAL_INCOME_VALUE: {
        columnName: 'BI/Rental Income Value',
        key: 'biRentalIncomeValue',
        required: true,
        inputType: 'currency',
        mask: numberMask,
        validationSort: [
            ['biRentalIncomeValue', 'personalPropertyValue'],
            ['biRentalIncomeValue', 'otherValue'],
            ['biRentalIncomeValue', 'grossSales'],
            ['biRentalIncomeValue', 'plotSizeAcres'],
        ],
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : 0))
            .min(0)
            .when(
                ['buildingValue', 'personalPropertyValue', 'otherValue', 'grossSales', 'plotSizeAcres'],
                (v1: number, v2: number, v3: number, v4: number, v5: number, schema: Yup.NumberSchema) => {
                    return schema.test({
                        test: (bv?: number) => checkSomeValue(v1, v2, v3, v4, v5, bv),
                        message: 'at least one value or sales or acres must have data',
                    });
                }
            ),
        columnDef: {
            headerName: 'BI/Rental Income Value',
            field: 'biRentalIncomeValue',
            width: 160,
        },
    },
    PERSONAL_PROPERTY_VALUE: {
        columnName: 'Personal Property Value',
        key: 'personalPropertyValue',
        required: true,
        inputType: 'currency',
        mask: numberMask,
        validationSort: [
            ['personalPropertyValue', 'otherValue'],
            ['personalPropertyValue', 'grossSales'],
            ['personalPropertyValue', 'plotSizeAcres'],
        ],
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : 0))
            .min(0)
            .when(
                ['buildingValue', 'biRentalIncomeValue', 'otherValue', 'grossSales', 'plotSizeAcres'],
                (v1: number, v2: number, v3: number, v4: number, v5: number, schema: Yup.NumberSchema) => {
                    return schema.test({
                        test: (bv?: number) => checkSomeValue(v1, v2, v3, v4, v5, bv),
                        message: 'at least one value or sales or acres must have data',
                    });
                }
            ),
        columnDef: {
            headerName: 'Personal Property Value',
            field: 'personalPropertyValue',
            width: 160,
        },
    },
    OTHER_VALUE: {
        columnName: 'Other Value',
        key: 'otherValue',
        required: true,
        inputType: 'currency',
        mask: numberMask,
        validationSort: [
            ['otherValue', 'grossSales'],
            ['otherValue', 'plotSizeAcres'],
        ],
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : 0))
            .min(0)
            .when(
                ['buildingValue', 'biRentalIncomeValue', 'personalPropertyValue', 'grossSales', 'plotSizeAcres'],
                (v1: number, v2: number, v3: number, v4: number, v5: number, schema: Yup.NumberSchema) => {
                    return schema.test({
                        test: (bv?: number) => checkSomeValue(v1, v2, v3, v4, v5, bv),
                        message: 'at least one value or sales or acres must have data',
                    });
                }
            ),
        columnDef: {
            headerName: 'Other Value',
            field: 'otherValue',
            width: 160,
        },
    },
    TOTAL_INSURABLE_VALUE: {
        columnName: 'TIV',
        key: 'tiv',
        valueGetter: (data) => {
            const matchOther = (data?.otherValue?.toString() || '0').match(/[0-9,.]+/)[0];
            const matchPersonalPropertyValue = (data?.personalPropertyValue?.toString() || '0').match(/[0-9,.]+/)[0];
            const matchBiRentalIncomeValue = (data?.biRentalIncomeValue?.toString() || '0').match(/[0-9,.]+/)[0];
            const matchBuildingValue = (data?.buildingValue?.toString() || '0').match(/[0-9,.]+/)[0];
            return (
                parseLocaleNumber(matchOther || '0') +
                parseLocaleNumber(matchPersonalPropertyValue || '0') +
                parseLocaleNumber(matchBiRentalIncomeValue || '0') +
                parseLocaleNumber(matchBuildingValue || '0')
            );
        },
        disabled: true,
        required: false,
        inputType: 'currency',
        mask: numberMask,
        columnDef: {
            headerName: 'TIV',
            width: 160,
            valueGetter: (params: ValueGetterParams) => {
                const data = params.data;
                const matchOther = (data?.otherValue?.toString() || '0').match(/[0-9,.]+/)[0];
                const matchPersonalPropertyValue = (data?.personalPropertyValue?.toString() || '0').match(
                    /[0-9,.]+/
                )[0];
                const matchBiRentalIncomeValue = (data?.biRentalIncomeValue?.toString() || '0').match(/[0-9,.]+/)[0];
                const matchBuildingValue = (data?.buildingValue?.toString() || '0').match(/[0-9,.]+/)[0];
                return (
                    parseLocaleNumber(matchOther || '0') +
                    parseLocaleNumber(matchPersonalPropertyValue || '0') +
                    parseLocaleNumber(matchBiRentalIncomeValue || '0') +
                    parseLocaleNumber(matchBuildingValue || '0')
                );
            },
        },
    },
    INSURANCE_TO_VALUE: {
        columnName: 'ITV',
        key: 'itv',
        required: false,
        mask: numberMask,
        disabled: true,
        valueGetter: (data) => {
            const matchTSF = (data?.totalSquareFootage?.toString() || '').match(/[0-9,.]+/);
            const matchBV = (data?.buildingValue?.toString() || '').match(/[0-9,.]+/);
            if (matchTSF && parseLocaleNumber(matchTSF[0]) > 0) {
                if (matchBV && parseLocaleNumber(matchBV[0]) > 0) {
                    return parseLocaleNumber(matchBV[0]) / parseLocaleNumber(matchTSF[0]);
                }
            }
            return 0;
        },
        columnDef: {
            headerName: 'ITV',
            width: 110,
            valueGetter: (params: ValueGetterParams) => {
                const data = params.data;
                const matchTSF = (data?.totalSquareFootage?.toString() || '').match(/[0-9,.]+/);
                const matchBV = (data?.buildingValue?.toString() || '').match(/[0-9,.]+/);
                if (matchTSF && parseLocaleNumber(matchTSF[0]) > 0) {
                    if (matchBV && parseLocaleNumber(matchBV[0]) > 0) {
                        return parseLocaleNumber(matchBV[0]) / parseLocaleNumber(matchTSF[0]);
                    }
                }
                return 0;
            },
        },
    },
    GROSS_SALES: {
        columnName: 'Gross Sales',
        key: 'grossSales',
        required: true,
        inputType: 'currency',
        mask: numberMask,
        validationSort: [['grossSales', 'plotSizeAcres']],
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : 0))
            .min(0)
            .when(
                ['buildingValue', 'biRentalIncomeValue', 'personalPropertyValue', 'otherValue', 'plotSizeAcres'],
                (v1: number, v2: number, v3: number, v4: number, v5: number, schema: Yup.NumberSchema) => {
                    return schema.test({
                        test: (bv?: number) => checkSomeValue(v1, v2, v3, v4, v5, bv),
                        message: 'at least one value or sales or acres must have data',
                    });
                }
            ),
        columnDef: {
            headerName: 'Gross Sales',
            field: 'grossSales',
            width: 160,
        },
    },
    PLOT_SIZE_ACRES: {
        columnName: '# Acres',
        key: 'plotSizeAcres',
        required: true,
        mask: numberMask,
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : 0))
            .min(0)
            .when(
                ['buildingValue', 'biRentalIncomeValue', 'personalPropertyValue', 'otherValue', 'grossSales'],
                (v1: number, v2: number, v3: number, v4: number, v5: number, schema: Yup.NumberSchema) => {
                    return schema.test({
                        test: (bv?: number) => checkSomeValue(v1, v2, v3, v4, v5, bv),
                        message: 'at least one value or sales or acres must have data',
                    });
                }
            ),
        columnDef: {
            headerName: '# Acres',
            field: 'plotSizeAcres',
            width: 160,
        },
    },
    MORTGAGEE: {
        columnName: 'Mortgagee',
        key: 'mortgagee',
        width: 130,
        required: false,
        inputType: 'multiline',
        validation: Yup.string().max(1024, 'Please limit to 1024 characters').nullable(),
        columnDef: {
            headerName: 'Mortgagee',
            field: 'mortgagee',
            width: 130,
        },
    },
    LOSS_PAYEE: {
        columnName: 'Loss Payee',
        key: 'lossPayee',
        width: 130,
        required: false,
        validation: Yup.string().max(1024, 'Please limit to 1024 characters').nullable(),
        inputType: 'multiline',
        columnDef: {
            headerName: 'Loss Payee',
            field: 'lossPayee',
            width: 130,
        },
    },
    ADDITIONAL_NAMED_INSURED: {
        columnName: 'Additional Name Insured(s)',
        key: 'additionalNamedInsured',
        width: 130,
        required: false,
        validation: Yup.string().max(1024, 'Please limit to 1024 characters').nullable(),
        inputType: 'multiline',
        columnDef: {
            headerName: 'Additional Named Insured(s)',
            field: 'additionalNamedInsured',
            width: 130,
        },
    },
    ADDITIONAL_INTERESTS: {
        columnName: 'Additional Interest(s)',
        key: 'additionalInterests',
        width: 130,
        required: false,
        validation: Yup.string().max(1024, 'Please limit to 1024 characters').nullable(),
        inputType: 'multiline',
        columnDef: {
            headerName: 'Additional Interests(s)',
            field: 'additionalInterests',
            width: 130,
        },
    },
    ROOF_SQUARE_FOOTAGE: {
        columnName: 'Roof Square Footage',
        key: 'roofSquareFootage',
        required: true,
        mask: numberMask,
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .min(0)
            .nullable(true),
        columnDef: {
            headerName: 'Roof Square Footage',
            field: 'roofSquareFootage',
            width: 125,
        },
    },
    ROOF_COST: {
        columnName: 'Est Roof Cost',
        key: 'roofCost',
        required: true,
        mask: numberMask,
        inputType: 'currency',
        validation: Yup.number()
            .typeError('Please enter a number')
            .transform((_, n) => (n ? parseLocaleNumber(n) : null))
            .min(0)
            .nullable(true),
        columnDef: {
            headerName: 'Est Roof Cost',
            field: 'roofCost',
            width: 125,
        },
    },
});

function parseLocaleNumber(str: string | number) {
    // Detect the user's locale decimal separator:
    const decimalSeparator = (1.1).toLocaleString().substring(1, 2);
    // Detect the user's locale thousand separator:
    let thousandSeparator = (1000).toLocaleString().substring(1, 2);
    // In case there are locales that don't use a thousand separator
    if (thousandSeparator.match(/\d/)) {
        thousandSeparator = '';
    }

    if (typeof str !== 'string') {
        return str;
    }

    let str1 = str as string;
    str1 = str1
        .replace(new RegExp('[' + thousandSeparator + ']', 'g'), '')
        .replace(new RegExp('[' + decimalSeparator + ']'), '.');

    return parseFloat(str1);
}

export function getSOVEditColumns(
    locationOptions: LocationOptionsData,
    disabledColumns?: PropertyColumn[]
): Record<PropertyColumn, DataGridColumn> {
    const ret = columnDetails(locationOptions);

    for (const key in ret) {
        if (ret.hasOwnProperty(key)) {
            const detail = ret[key];
            detail.columnDef.colId = key;
            if (disabledColumns && disabledColumns.indexOf(key as PropertyColumn) >= 0) {
                // this column is disabled
                detail.disabled = true;
            }
        }
    }
    return ret;
}
